import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { authService, rolesService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    HiOutlineUsers,
    HiOutlinePlus,
    HiOutlinePencil,
    HiOutlineTrash,
    HiOutlineX,
    HiOutlineCheckCircle,
    HiOutlineInformationCircle,
    HiOutlineShieldCheck,
    HiOutlineMail,
    HiOutlineKey,
    HiOutlineTemplate,
    HiOutlineCalendar,
    HiOutlineSpeakerphone,
    HiOutlineDocumentText,
    HiOutlinePhotograph,
    HiOutlineUserGroup
} from 'react-icons/hi';
import './EventsManage.css';

const UsersManage = () => {
    const [users, setUsers] = useState([]);
    const [customRoles, setCustomRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [useCustomPermissions, setUseCustomPermissions] = useState(false);
    const { user: currentUser } = useAuth();
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        full_name: '',
        permissions: [],
        is_active: true
    });

    const availablePermissions = [
        { value: 'events', label: 'Eventos', description: 'Gestionar eventos', icon: HiOutlineCalendar },
        { value: 'announcements', label: 'Convocatorias', description: 'Gestionar convocatorias', icon: HiOutlineSpeakerphone },
        { value: 'blog', label: 'Blog', description: 'Gestionar artículos de blog', icon: HiOutlineDocumentText },
        { value: 'banners', label: 'Banners', description: 'Gestionar banners', icon: HiOutlinePhotograph },
        { value: 'users', label: 'Usuarios', description: 'Gestionar usuarios (Admin)', icon: HiOutlineUserGroup }
    ];

    useEffect(() => {
        loadUsers();
        loadCustomRoles();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await authService.getUsers();
            setUsers(response.data);
        } catch (error) {
            toast.error('Error al cargar usuarios');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadCustomRoles = async () => {
        try {
            const response = await rolesService.getAll();
            setCustomRoles(response.data);
        } catch (error) {
            console.error('Error al cargar roles personalizados:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePermissionToggle = (permission) => {
        setFormData(prev => {
            const permissions = prev.permissions || [];
            const hasPermission = permissions.includes(permission);
            
            return {
                ...prev,
                permissions: hasPermission
                    ? permissions.filter(p => p !== permission)
                    : [...permissions, permission]
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (editingUser) {
                await authService.updateUser(editingUser.id, formData);
                toast.success('Usuario actualizado exitosamente');
            } else {
                await authService.createUser(formData);
                toast.success('Usuario creado exitosamente');
            }

            resetForm();
            loadUsers();
            setShowModal(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al guardar usuario');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            email: user.email,
            password: '',
            full_name: user.full_name,
            permissions: Array.isArray(user.permissions) ? user.permissions : [],
            is_active: Boolean(user.is_active)
        });
        setShowModal(true);
    };

    const handleDelete = async (id, full_name) => {
        if (id === currentUser.id) {
            toast.error('No puedes eliminar tu propia cuenta');
            return;
        }

        if (!window.confirm(`¿Estás seguro de eliminar al usuario "${full_name}"?`)) {
            return;
        }

        try {
            await authService.deleteUser(id);
            toast.success('Usuario eliminado exitosamente');
            loadUsers();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al eliminar usuario');
            console.error(error);
        }
    };

    const resetForm = () => {
        setFormData({
            email: '',
            password: '',
            full_name: '',
            permissions: [],
            is_active: true
        });
        setEditingUser(null);
    };

    const getPermissionBadges = (permissions) => {
        if (!permissions || permissions.length === 0) {
            return (
                <div className="table-cell-content">
                    <span className="role-badge role-viewer">Sin permisos</span>
                </div>
            );
        }

        const permissionLabels = {
            events: { label: 'Eventos', class: 'role-events' },
            announcements: { label: 'Convocatorias', class: 'role-announcements' },
            blog: { label: 'Blog', class: 'role-blog' },
            banners: { label: 'Banners', class: 'role-banners' },
            users: { label: 'Admin', class: 'role-admin' }
        };

        return (
            <div className="table-cell-content">
                {permissions.map((permission, index) => {
                    const permInfo = permissionLabels[permission] || { label: permission, class: 'role-viewer' };
                    return (
                        <span key={index} className={`role-badge ${permInfo.class}`}>
                            <HiOutlineShieldCheck />
                            {permInfo.label}
                        </span>
                    );
                })}
            </div>
        );
    };

    if (loading && users.length === 0) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando usuarios...</p>
            </div>
        );
    }

    return (
        <div className="content-manage">
            <div className="content-header">
                <div className="header-info">
                    <h1>
                        Gestión de Usuarios
                    </h1>
                    <p>Administra los usuarios y permisos del sistema CMS</p>
                </div>
                <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                    <span>Nuevo Usuario</span>
                </button>
            </div>

            {users.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <HiOutlineUsers />
                    </div>
                    <h3>No hay usuarios registrados</h3>
                    <p>Comienza creando el primer usuario del sistema</p>
                </div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Nombre Completo</th>
                                <th>Email</th>
                                <th>Permisos</th>
                                <th>Estado</th>
                                <th>Fecha de Registro</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td data-label="Nombre">{user.full_name}</td>
                                    <td data-label="Email">{user.email}</td>
                                    <td data-label="Permisos">{getPermissionBadges(user.permissions)}</td>
                                    <td data-label="Estado">
                                        {user.is_active ? (
                                            <span className="status-badge status-active">Activo</span>
                                        ) : (
                                            <span className="status-badge status-inactive">Inactivo</span>
                                        )}
                                    </td>
                                    <td data-label="Registro">{new Date(user.created_at).toLocaleDateString('es-ES', { timeZone: 'UTC' })}</td>
                                    <td>
                                        <div className="table-actions">
                                            <button
                                                className="btn-icon btn-secondary"
                                                onClick={() => handleEdit(user)}
                                                title="Editar usuario"
                                            >
                                                <HiOutlinePencil />
                                            </button>
                                            <button
                                                className="btn-icon btn-danger"
                                                onClick={() => handleDelete(user.id, user.full_name)}
                                                title="Eliminar usuario"
                                                disabled={user.id === currentUser.id}
                                            >
                                                <HiOutlineTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => { resetForm(); setShowModal(false); }}>
                    <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">
                                <HiOutlineUsers />
                                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                            </h2>
                            <button className="modal-close" onClick={() => { resetForm(); setShowModal(false); }}>
                                <HiOutlineX />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert-info">
                                    <HiOutlineInformationCircle />
                                    <span>
                                        {editingUser
                                            ? 'Actualiza la información del usuario. Deja la contraseña en blanco para mantener la actual.'
                                            : 'Crea un nuevo usuario y asígnale los permisos específicos que necesita.'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2">
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">
                                            Email <span className="required">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="usuario@ejemplo.com"
                                            required
                                            disabled={editingUser}
                                        />
                                        {editingUser && (
                                            <small className="form-hint">El email no puede modificarse</small>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">
                                            Nombre Completo <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="full_name"
                                            className="form-control"
                                            value={formData.full_name}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Juan Pérez García"
                                            required
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">
                                            Contraseña {editingUser ? '(dejar en blanco para no cambiar)' : <span className="required">*</span>}
                                        </label>
                                        <input
                                            type="password"
                                            name="password"
                                            className="form-control"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="Mínimo 6 caracteres"
                                            minLength={6}
                                            required={!editingUser}
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">
                                            Permisos <span className="required">*</span>
                                        </label>
                                        
                                        {/* Toggle entre roles predefinidos y permisos personalizados */}
                                        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
                                            <button
                                                type="button"
                                                className={`btn ${!useCustomPermissions ? 'btn-primary' : 'btn-secondary'}`}
                                                onClick={() => setUseCustomPermissions(false)}
                                                style={{ flex: 1 }}
                                            >
                                                <HiOutlineTemplate />
                                                Roles Predefinidos
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${useCustomPermissions ? 'btn-primary' : 'btn-secondary'}`}
                                                onClick={() => setUseCustomPermissions(true)}
                                                style={{ flex: 1 }}
                                            >
                                                <HiOutlineShieldCheck />
                                                Permisos Personalizados
                                            </button>
                                        </div>

                                        {!useCustomPermissions ? (
                                            /* Selector de roles predefinidos */
                                            <div>
                                                <select
                                                    className="form-control"
                                                    onChange={(e) => {
                                                        const selectedRole = customRoles.find(r => r.id === parseInt(e.target.value));
                                                        if (selectedRole) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                permissions: selectedRole.permissions
                                                            }));
                                                        }
                                                    }}
                                                    style={{ marginBottom: '12px' }}
                                                >
                                                    <option value="">Selecciona un rol predefinido</option>
                                                    {customRoles.map(role => (
                                                        <option key={role.id} value={role.id}>
                                                            {role.name} - {role.description}
                                                        </option>
                                                    ))}
                                                </select>
                                                
                                                {formData.permissions.length > 0 && (
                                                    <div style={{
                                                        padding: '12px',
                                                        background: 'var(--gray-50)',
                                                        borderRadius: 'var(--radius-md)',
                                                        border: '1px solid var(--gray-200)'
                                                    }}>
                                                        <strong>Permisos seleccionados:</strong>
                                                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                            {formData.permissions.map((perm, idx) => {
                                                                const permInfo = availablePermissions.find(p => p.value === perm);
                                                                return (
                                                                    <span key={idx} style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        padding: '4px 12px',
                                                                        background: 'white',
                                                                        borderRadius: '12px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {permInfo?.icon && <permInfo.icon />} {permInfo?.label}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* Checkboxes de permisos personalizados */
                                            <div style={{ 
                                                display: 'grid', 
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                                                gap: '12px',
                                                padding: '16px',
                                                background: 'var(--gray-50)',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--gray-200)'
                                            }}>
                                                {availablePermissions.map(permission => (
                                                    <label 
                                                        key={permission.value} 
                                                        className="checkbox-label"
                                                        style={{
                                                            padding: '12px',
                                                            background: 'white',
                                                            borderRadius: 'var(--radius-md)',
                                                            border: '2px solid',
                                                            borderColor: formData.permissions.includes(permission.value) 
                                                                ? 'var(--primary)' 
                                                                : 'var(--gray-200)',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.permissions.includes(permission.value)}
                                                                onChange={() => handlePermissionToggle(permission.value)}
                                                            />
                                                            <permission.icon style={{ fontSize: '1.2rem' }} />
                                                            <strong>{permission.label}</strong>
                                                        </div>
                                                        <small style={{ 
                                                            color: 'var(--gray-600)', 
                                                            fontSize: '0.75rem',
                                                            marginLeft: '28px' 
                                                        }}>
                                                            {permission.description}
                                                        </small>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                        
                                        <small className="form-hint">
                                            {useCustomPermissions 
                                                ? 'Selecciona los permisos individuales que tendrá este usuario.'
                                                : 'Elige un rol predefinido o cambia a permisos personalizados.'}
                                        </small>
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <span>Usuario activo</span>
                                        </label>
                                        <small className="form-hint">
                                            Los usuarios inactivos no pueden iniciar sesión
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => { resetForm(); setShowModal(false); }}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    <HiOutlineCheckCircle />
                                    {loading ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Crear Usuario')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersManage;

